import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Check, X, Crown, Zap, Building2, Users, Mail,
  ArrowRight, Sparkles, Shield, Target, FileText,
  Clock, Headphones, Server, Lock, Globe, Bot, Rocket,
  CreditCard, Loader2, Cpu, Database, HardDrive, Brain,
  MapPin, ShoppingCart, Cloud, Network, PenTool
} from 'lucide-react';
import { billing } from '../../services/api/client';
import { StripePaymentStatus } from '../../components/StripeCheckout';

const PricingPage = () => {
  const plans = [
    {
      id: 'free',
      stripeProductId: 'prod_Tey0j9vmHgjFai',
      name: 'Gratuit',
      tagline: 'Pour découvrir la plateforme',
      priceMonthly: 0,
      priceYearly: 0,
      highlighted: false,
      resources: {
        projets: '1',
        scans: '2/mois',
        aiRequests: '10/jour',
        vram: '2Go',
        membres: '1',
        domaines: '0',
        activeDirectory: '0',
        llmModels: '1'
      },
      features: [
        { name: 'Rétention 15 jours', included: true },
        { name: 'Export PDF', included: true },
        { name: 'Signature électronique (eIDAS)', included: true },
        { name: 'Support Communautaire', included: true },
        { name: 'API', included: false },
        { name: 'SSO/SAML', included: false },
      ],
      cta: 'Commencer gratuitement',
      icon: Globe,
      color: 'gray'
    },
    {
      id: 'pro',
      stripeProductId: 'prod_TexnXAQiZaHM0q',
      name: 'Professionnel',
      tagline: 'Pour les indépendants et TPE',
      priceMonthly: null,
      priceYearly: null,
      highlighted: true,
      badge: 'Recommandé',
      isCustom: true,
      resources: {
        projets: '10',
        scans: '50/mois',
        aiRequests: '100/jour',
        vram: '6Go',
        membres: '3',
        domaines: '2',
        activeDirectory: '1',
        llmModels: '2'
      },
      features: [
        { name: 'Rétention 3 mois', included: true },
        { name: 'Export PDF, JSON, TXT', included: true },
        { name: 'Signature électronique (eIDAS)', included: true },
        { name: 'Support Email (48h)', included: true },
        { name: 'API lecture seule', included: true },
        { name: 'SSO/SAML', included: false },
      ],
      cta: 'Demander un devis',
      icon: Zap,
      color: 'green'
    },
    {
      id: 'entreprise',
      stripeProductId: 'prod_Texu1jlXpfll8N',
      name: 'Entreprise',
      tagline: 'Pour les PME en croissance',
      priceMonthly: null,
      priceYearly: null,
      highlighted: false,
      isCustom: true,
      resources: {
        projets: '50',
        scans: '200/mois',
        aiRequests: '500/jour',
        vram: '16Go',
        membres: '10',
        domaines: '5',
        activeDirectory: '5',
        llmModels: '3'
      },
      features: [
        { name: 'Rétention 12 mois', included: true },
        { name: 'Export PDF, JSON, TXT, DOCX', included: true },
        { name: 'Signature électronique (eIDAS)', included: true },
        { name: 'Support Prioritaire (4h)', included: true },
        { name: 'API accès complet', included: true },
        { name: 'Intégration CI/CD', included: true },
      ],
      cta: 'Demander un devis',
      icon: Building2,
      color: 'purple'
    },
    {
      id: 'groupe',
      stripeProductId: 'prod_Texu5AFcj9vnyO',
      name: 'Groupe',
      tagline: 'Pour les grandes structures',
      priceMonthly: null,
      priceYearly: null,
      highlighted: false,
      isCustom: true,
      resources: {
        projets: '100',
        scans: '1 000/mois',
        aiRequests: '1 000/jour',
        vram: '32Go',
        membres: '50',
        domaines: '20',
        activeDirectory: '20',
        llmModels: '5'
      },
      features: [
        { name: 'Rétention 24 mois', included: true },
        { name: 'Export tous formats + PowerPoint', included: true },
        { name: 'Signature électronique (eIDAS)', included: true },
        { name: 'Gestionnaire de compte dédié', included: true },
        { name: 'SSO / SAML', included: true },
        { name: 'API accès complet', included: true },
      ],
      cta: 'Demander un devis',
      icon: Users,
      color: 'cyan'
    },
    {
      id: 'dedie',
      stripeProductId: 'prod_TexyQMODffHVuP',
      name: 'Dédié',
      tagline: 'Infrastructure sur mesure',
      priceMonthly: null,
      priceYearly: null,
      highlighted: false,
      isCustom: true,
      resources: {
        projets: 'Illimité',
        scans: 'Illimité',
        aiRequests: 'Illimité',
        vram: '16Go',
        membres: 'Illimité',
        domaines: 'Illimité',
        activeDirectory: 'Illimité',
        llmModels: 'Illimité'
      },
      features: [
        { name: 'Rétention illimitée', included: true },
        { name: 'Infrastructure isolée', included: true },
        { name: 'Souveraineté des données', included: true },
        { name: 'Signature électronique (eIDAS)', included: true },
        { name: 'Support 24/7 Téléphone', included: true },
        { name: 'SLA Personnalisé', included: true },
      ],
      cta: 'Demander un devis',
      ctaLink: '/dedicated-server',
      icon: Server,
      color: 'orange'
    }
  ];

  const infrastructureOptions = [
    {
      icon: MapPin,
      title: 'Location Dédiée',
      description: 'Serveur dédié dans notre datacenter souverain à Reims, France',
      color: 'blue'
    },
    {
      icon: ShoppingCart,
      title: 'Achat Matériel',
      description: 'Acquisition du matériel avec installation et configuration incluses',
      color: 'green'
    },
    {
      icon: Cloud,
      title: 'PaaS',
      description: 'Plateforme en tant que service - Solution cloud managée et supervisée',
      color: 'purple'
    },
    {
      icon: Network,
      title: 'Sur site',
      description: 'Installation dans votre infrastructure avec support technique dédié',
      color: 'orange'
    }
  ];

  const colorClasses = {
    gray: {
      bg: 'bg-gray-500/10',
      border: 'border-gray-500/30',
      text: 'text-gray-400',
      button: 'bg-gray-700 hover:bg-gray-600 text-white',
      glow: ''
    },
    green: {
      bg: 'bg-brand-green/10',
      border: 'border-brand-green/30',
      text: 'text-brand-green',
      button: 'bg-brand-green hover:bg-brand-green/90 text-black',
      glow: 'shadow-lg shadow-brand-green/20'
    },
    purple: {
      bg: 'bg-purple-500/10',
      border: 'border-purple-500/30',
      text: 'text-purple-400',
      button: 'bg-purple-600 hover:bg-purple-500 text-white',
      glow: ''
    },
    cyan: {
      bg: 'bg-cyan-500/10',
      border: 'border-cyan-500/30',
      text: 'text-cyan-400',
      button: 'bg-cyan-600 hover:bg-cyan-500 text-white',
      glow: ''
    },
    orange: {
      bg: 'bg-orange-500/10',
      border: 'border-orange-500/30',
      text: 'text-orange-400',
      button: 'bg-orange-600 hover:bg-orange-500 text-white',
      glow: ''
    }
  };

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [annual, setAnnual] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [error, setError] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  useEffect(() => {
    if (searchParams.get('success') === 'true') {
      setPaymentSuccess(true);
    }
    if (searchParams.get('welcome') === 'true') {
      setShowWelcome(true);
      setTimeout(() => {
        window.history.replaceState({}, '', '/pricing');
      }, 100);
    }
  }, [searchParams]);

  const handleSelectPlan = async (planId) => {
    if (planId === 'free') {
      navigate('/dashboard');
      return;
    }

    // Custom plans redirect to their specific link or contact
    const plan = plans.find(p => p.id === planId);
    if (plan?.isCustom) {
      navigate(plan.ctaLink || '/contact');
      return;
    }

    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    if (!token) {
      navigate(`/login?redirect=/pricing&plan=${planId}&billing=${annual ? 'yearly' : 'monthly'}`);
      return;
    }

    setLoadingPlan(planId);
    setError(null);

    try {
      const billingInterval = annual ? 'yearly' : 'monthly';
      const response = await billing.createCheckoutSession(planId, billingInterval);

      if (response.checkout_url) {
        window.location.href = response.checkout_url;
      } else {
        throw new Error('Erreur lors de la création de la session de paiement');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.response?.data?.detail || 'Une erreur est survenue. Veuillez réessayer.');
      setLoadingPlan(null);
    }
  };

  const formatPrice = (price) => {
    if (price === null) return 'Sur devis';
    if (price === 0) return 'Gratuit';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(price);
  };

  return (
    <main className="min-h-screen bg-[#0a0a0a]">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-brand-green/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-purple-500/5 rounded-full blur-[100px]" />
      </div>

      {paymentSuccess && <StripePaymentStatus success={true} />}

      <div className="container mx-auto px-6 py-12 relative z-10">
        {/* Error Banner */}
        {error && (
          <div className="mb-8 bg-red-500/10 rounded-2xl p-6 border border-red-500/30">
            <div className="flex items-center justify-center gap-4">
              <div className="text-center">
                <h2 className="text-xl font-bold text-red-400">Erreur de paiement</h2>
                <p className="text-gray-300">{error}</p>
              </div>
              <button
                onClick={() => setError(null)}
                className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-all"
              >
                Fermer
              </button>
            </div>
          </div>
        )}

        {/* Welcome Banner */}
        {showWelcome && (
          <div className="mb-8 bg-gradient-to-r from-brand-green/20 to-emerald-500/20 rounded-2xl p-6 border border-brand-green/30 animate-pulse">
            <div className="flex items-center justify-center gap-4">
              <Sparkles className="w-8 h-8 text-brand-green" />
              <div className="text-center">
                <h2 className="text-xl font-bold text-white">Bienvenue sur SYLink Pentest !</h2>
                <p className="text-gray-300">Choisissez le plan adapté à vos besoins</p>
              </div>
              <Sparkles className="w-8 h-8 text-brand-green" />
            </div>
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Choisissez votre <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-green to-emerald-400">plan</span>
          </h1>
          <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
            Automatisez vos tests de pénétration avec l'IA. Du freelance à l'entreprise, nous avons le plan qu'il vous faut.
          </p>

          {/* Contact Banner */}
          <div className="inline-flex items-center gap-3 bg-[#111111] rounded-2xl px-6 py-3 border border-gray-800">
            <Mail className="w-5 h-5 text-brand-green" />
            <span className="text-gray-300">Tous nos plans sont personnalisés selon vos besoins</span>
          </div>
        </div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5 mb-16">
          {plans.map((plan) => {
            const colors = colorClasses[plan.color];
            const Icon = plan.icon;

            return (
              <div
                key={plan.id}
                className={`relative rounded-2xl p-6 border transition-all duration-300 hover:-translate-y-2 ${
                  plan.highlighted
                    ? `bg-gradient-to-b from-brand-green/10 to-[#0d0d0d] border-brand-green/50 ${colors.glow}`
                    : 'bg-[#0d0d0d] border-gray-800 hover:border-gray-700'
                }`}
              >
                {/* Badge */}
                {plan.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="inline-flex items-center gap-1 bg-brand-green text-black text-xs font-bold px-4 py-1.5 rounded-full shadow-lg">
                      <Sparkles className="w-3 h-3" />
                      {plan.badge}
                    </span>
                  </div>
                )}

                {/* Icon & Name */}
                <div className="text-center mb-4 pt-2">
                  <div className={`w-14 h-14 mx-auto mb-3 rounded-2xl ${colors.bg} border ${colors.border} flex items-center justify-center`}>
                    <Icon className={`w-7 h-7 ${colors.text}`} />
                  </div>
                  <h3 className="text-xl font-bold text-white">{plan.name}</h3>
                  <p className={`text-xs font-medium ${colors.text} mt-1`}>{plan.tagline}</p>
                </div>

                {/* Price */}
                <div className="text-center mb-4">
                  {plan.isCustom ? (
                    <div>
                      <div className="text-2xl font-bold text-white">Sur devis</div>
                      <span className="text-gray-500 text-sm">Tarif personnalisé</span>
                    </div>
                  ) : plan.priceMonthly === 0 ? (
                    <div>
                      <div className="text-2xl font-bold text-white">Gratuit</div>
                      <span className="text-gray-500 text-sm">Pour toujours</span>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-baseline justify-center gap-1">
                        <span className="text-3xl font-bold text-white">
                          {formatPrice(annual ? Math.round(plan.priceYearly / 12) : plan.priceMonthly)}
                        </span>
                      </div>
                      <span className="text-gray-500 text-sm">/mois HT</span>
                      {annual && plan.priceYearly && (
                        <p className="text-brand-green text-xs mt-1">
                          Facturé {formatPrice(plan.priceYearly)}/an
                        </p>
                      )}
                    </>
                  )}
                </div>

                {/* Resources */}
                <div className="bg-[#0a0a0a] rounded-xl p-3 mb-4 border border-gray-800/50">
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="flex items-center gap-1.5">
                      <Target className="w-3 h-3 text-brand-green" />
                      <span className="text-gray-400">{plan.resources.projets} projets</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Zap className="w-3 h-3 text-brand-green" />
                      <span className="text-gray-400">{plan.resources.scans}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Brain className="w-3 h-3 text-brand-green" />
                      <span className="text-gray-400">{plan.resources.aiRequests}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Cpu className="w-3 h-3 text-brand-green" />
                      <span className="text-gray-400">{plan.resources.vram} VRAM</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Users className="w-3 h-3 text-brand-green" />
                      <span className="text-gray-400">{plan.resources.membres} membres</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Globe className="w-3 h-3 text-brand-green" />
                      <span className="text-gray-400">{plan.resources.domaines} dom.</span>
                    </div>
                  </div>
                </div>

                {/* Features */}
                <ul className="space-y-2 mb-6">
                  {plan.features.slice(0, 6).map((feature, index) => (
                    <li key={index} className="flex items-start gap-2">
                      {feature.included ? (
                        <Check className="w-4 h-4 text-brand-green flex-shrink-0 mt-0.5" />
                      ) : (
                        <X className="w-4 h-4 text-gray-700 flex-shrink-0 mt-0.5" />
                      )}
                      <span className={`text-xs ${feature.included ? 'text-gray-300' : 'text-gray-600'}`}>
                        {feature.name}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* CTA Button */}
                <button
                  onClick={() => handleSelectPlan(plan.id)}
                  disabled={loadingPlan === plan.id}
                  className={`w-full py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-sm ${colors.button}`}
                >
                  {loadingPlan === plan.id ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Redirection...</span>
                    </>
                  ) : plan.isCustom ? (
                    <>
                      <Mail className="w-4 h-4" />
                      {plan.cta}
                    </>
                  ) : (
                    <>
                      {plan.cta}
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            );
          })}
        </div>

        {/* Comparison Table */}
        <div className="mb-16 overflow-x-auto">
          <h2 className="text-2xl font-bold text-white text-center mb-8">
            Comparatif détaillé
          </h2>
          <table className="w-full text-sm" style={{ minWidth: '700px' }}>
            <thead>
              <tr className="border-b border-gray-800">
                <th className="text-left py-4 px-4 text-gray-400 font-medium min-w-[200px]">Fonctionnalité</th>
                <th className="text-center py-4 px-3 text-gray-400 font-medium whitespace-nowrap">Gratuit</th>
                <th className="text-center py-4 px-3 text-brand-green font-medium whitespace-nowrap">Professionnel</th>
                <th className="text-center py-4 px-3 text-purple-400 font-medium whitespace-nowrap">Entreprise</th>
                <th className="text-center py-4 px-3 text-cyan-400 font-medium whitespace-nowrap">Groupe</th>
                <th className="text-center py-4 px-3 text-orange-400 font-medium whitespace-nowrap">Dédié</th>
              </tr>
            </thead>
            <tbody className="text-gray-300">
              <tr className="border-b border-gray-800/50">
                <td className="py-3 px-4 whitespace-nowrap">Prix</td>
                <td className="text-center py-3 px-3 whitespace-nowrap">Gratuit</td>
                <td className="text-center py-3 px-3 text-gray-400 whitespace-nowrap">Sur devis</td>
                <td className="text-center py-3 px-3 text-gray-400 whitespace-nowrap">Sur devis</td>
                <td className="text-center py-3 px-3 text-gray-400 whitespace-nowrap">Sur devis</td>
                <td className="text-center py-3 px-3 text-gray-400 whitespace-nowrap">Sur devis</td>
              </tr>
              <tr className="border-b border-gray-800/50">
                <td className="py-3 px-4 whitespace-nowrap">Projets</td>
                <td className="text-center py-3 px-3 whitespace-nowrap">1</td>
                <td className="text-center py-3 px-3 whitespace-nowrap">10</td>
                <td className="text-center py-3 px-3 whitespace-nowrap">50</td>
                <td className="text-center py-3 px-3 whitespace-nowrap">100</td>
                <td className="text-center py-3 px-3 whitespace-nowrap">∞</td>
              </tr>
              <tr className="border-b border-gray-800/50">
                <td className="py-3 px-4 whitespace-nowrap">Scans/mois</td>
                <td className="text-center py-3 px-3 whitespace-nowrap">2</td>
                <td className="text-center py-3 px-3 whitespace-nowrap">50</td>
                <td className="text-center py-3 px-3 whitespace-nowrap">200</td>
                <td className="text-center py-3 px-3 whitespace-nowrap">1&nbsp;000</td>
                <td className="text-center py-3 px-3 whitespace-nowrap">∞</td>
              </tr>
              <tr className="border-b border-gray-800/50">
                <td className="py-3 px-4 whitespace-nowrap">Requêtes IA/jour</td>
                <td className="text-center py-3 px-3 whitespace-nowrap">10</td>
                <td className="text-center py-3 px-3 whitespace-nowrap">100</td>
                <td className="text-center py-3 px-3 whitespace-nowrap">500</td>
                <td className="text-center py-3 px-3 whitespace-nowrap">1&nbsp;000</td>
                <td className="text-center py-3 px-3 whitespace-nowrap">∞</td>
              </tr>
              <tr className="border-b border-gray-800/50">
                <td className="py-3 px-4 whitespace-nowrap">VRAM GPU</td>
                <td className="text-center py-3 px-3 whitespace-nowrap">2Go</td>
                <td className="text-center py-3 px-3 whitespace-nowrap">6Go</td>
                <td className="text-center py-3 px-3 whitespace-nowrap">16Go</td>
                <td className="text-center py-3 px-3 whitespace-nowrap">32Go</td>
                <td className="text-center py-3 px-3 whitespace-nowrap">16Go</td>
              </tr>
              <tr className="border-b border-gray-800/50">
                <td className="py-3 px-4 whitespace-nowrap">Membres</td>
                <td className="text-center py-3 px-3 whitespace-nowrap">1</td>
                <td className="text-center py-3 px-3 whitespace-nowrap">3</td>
                <td className="text-center py-3 px-3 whitespace-nowrap">10</td>
                <td className="text-center py-3 px-3 whitespace-nowrap">50</td>
                <td className="text-center py-3 px-3 whitespace-nowrap">∞</td>
              </tr>
              <tr className="border-b border-gray-800/50">
                <td className="py-3 px-4 whitespace-nowrap">Domaines</td>
                <td className="text-center py-3 px-3 whitespace-nowrap">0</td>
                <td className="text-center py-3 px-3 whitespace-nowrap">2</td>
                <td className="text-center py-3 px-3 whitespace-nowrap">5</td>
                <td className="text-center py-3 px-3 whitespace-nowrap">20</td>
                <td className="text-center py-3 px-3 whitespace-nowrap">∞</td>
              </tr>
              <tr className="border-b border-gray-800/50">
                <td className="py-3 px-4 whitespace-nowrap">Active Directory</td>
                <td className="text-center py-3 px-3 whitespace-nowrap">0</td>
                <td className="text-center py-3 px-3 whitespace-nowrap">1</td>
                <td className="text-center py-3 px-3 whitespace-nowrap">5</td>
                <td className="text-center py-3 px-3 whitespace-nowrap">20</td>
                <td className="text-center py-3 px-3 whitespace-nowrap">∞</td>
              </tr>
              <tr className="border-b border-gray-800/50">
                <td className="py-3 px-4 whitespace-nowrap">Modèles LLM</td>
                <td className="text-center py-3 px-3 whitespace-nowrap">1</td>
                <td className="text-center py-3 px-3 whitespace-nowrap">2</td>
                <td className="text-center py-3 px-3 whitespace-nowrap">3</td>
                <td className="text-center py-3 px-3 whitespace-nowrap">5</td>
                <td className="text-center py-3 px-3 whitespace-nowrap">∞</td>
              </tr>
              <tr className="border-b border-gray-800/50">
                <td className="py-3 px-4 whitespace-nowrap">Rétention</td>
                <td className="text-center py-3 px-3 whitespace-nowrap">15j</td>
                <td className="text-center py-3 px-3 whitespace-nowrap">3 mois</td>
                <td className="text-center py-3 px-3 whitespace-nowrap">12 mois</td>
                <td className="text-center py-3 px-3 whitespace-nowrap">24 mois</td>
                <td className="text-center py-3 px-3 whitespace-nowrap">∞</td>
              </tr>
              <tr className="border-b border-gray-800/50">
                <td className="py-3 px-4 whitespace-nowrap">Signature eIDAS</td>
                <td className="text-center py-3 px-3"><Check className="w-4 h-4 text-brand-green mx-auto" /></td>
                <td className="text-center py-3 px-3"><Check className="w-4 h-4 text-brand-green mx-auto" /></td>
                <td className="text-center py-3 px-3"><Check className="w-4 h-4 text-brand-green mx-auto" /></td>
                <td className="text-center py-3 px-3"><Check className="w-4 h-4 text-brand-green mx-auto" /></td>
                <td className="text-center py-3 px-3"><Check className="w-4 h-4 text-brand-green mx-auto" /></td>
              </tr>
              <tr className="border-b border-gray-800/50">
                <td className="py-3 px-4 whitespace-nowrap">API</td>
                <td className="text-center py-3 px-3"><X className="w-4 h-4 text-gray-600 mx-auto" /></td>
                <td className="text-center py-3 px-3 text-gray-400 whitespace-nowrap">Lecture seule</td>
                <td className="text-center py-3 px-3"><Check className="w-4 h-4 text-brand-green mx-auto" /></td>
                <td className="text-center py-3 px-3"><Check className="w-4 h-4 text-brand-green mx-auto" /></td>
                <td className="text-center py-3 px-3"><Check className="w-4 h-4 text-brand-green mx-auto" /></td>
              </tr>
              <tr className="border-b border-gray-800/50">
                <td className="py-3 px-4 whitespace-nowrap">SSO/SAML</td>
                <td className="text-center py-3 px-3"><X className="w-4 h-4 text-gray-600 mx-auto" /></td>
                <td className="text-center py-3 px-3"><X className="w-4 h-4 text-gray-600 mx-auto" /></td>
                <td className="text-center py-3 px-3"><X className="w-4 h-4 text-gray-600 mx-auto" /></td>
                <td className="text-center py-3 px-3"><Check className="w-4 h-4 text-brand-green mx-auto" /></td>
                <td className="text-center py-3 px-3"><Check className="w-4 h-4 text-brand-green mx-auto" /></td>
              </tr>
              <tr className="border-b border-gray-800/50">
                <td className="py-3 px-4 whitespace-nowrap">Support</td>
                <td className="text-center py-3 px-3 text-xs whitespace-nowrap">Communautaire</td>
                <td className="text-center py-3 px-3 text-xs whitespace-nowrap">Email 48h</td>
                <td className="text-center py-3 px-3 text-xs whitespace-nowrap">Prioritaire 4h</td>
                <td className="text-center py-3 px-3 text-xs whitespace-nowrap">Dédié</td>
                <td className="text-center py-3 px-3 text-xs whitespace-nowrap">24/7 Dédié</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Features Section */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-white text-center mb-8">
            Pourquoi choisir SYLink Pentest&#x202F;?
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-[#0d0d0d] rounded-xl p-6 border border-gray-800 hover:border-brand-green/30 transition-colors">
              <div className="w-12 h-12 rounded-xl bg-brand-green/10 flex items-center justify-center mb-4">
                <Bot className="w-6 h-6 text-brand-green" />
              </div>
              <h3 className="text-white font-semibold mb-2">IA de pointe</h3>
              <p className="text-gray-400 text-sm">
                Modèles LLM spécialisés (Llama, Qwen, DeepSeek) pour une analyse intelligente et des recommandations contextuelles.
              </p>
            </div>
            <div className="bg-[#0d0d0d] rounded-xl p-6 border border-gray-800 hover:border-brand-green/30 transition-colors">
              <div className="w-12 h-12 rounded-xl bg-brand-green/10 flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-brand-green" />
              </div>
              <h3 className="text-white font-semibold mb-2">45+ outils intégrés</h3>
              <p className="text-gray-400 text-sm">
                Nmap, Nuclei, Metasploit, Burp, et plus. Orchestration automatique selon vos cibles et objectifs.
              </p>
            </div>
            <div className="bg-[#0d0d0d] rounded-xl p-6 border border-gray-800 hover:border-brand-green/30 transition-colors">
              <div className="w-12 h-12 rounded-xl bg-brand-green/10 flex items-center justify-center mb-4">
                <FileText className="w-6 h-6 text-brand-green" />
              </div>
              <h3 className="text-white font-semibold mb-2">Rapports professionnels</h3>
              <p className="text-gray-400 text-sm">
                Rapports PDF, Word, Excel personnalisables. Marque blanche disponible pour les plans Entreprise+.
              </p>
            </div>
          </div>
        </div>

        {/* Infrastructure Options */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-white text-center mb-4">
            Options d'infrastructure
          </h2>
          <p className="text-gray-400 text-center max-w-2xl mx-auto mb-8">
            Choisissez le mode de déploiement adapté à vos besoins et contraintes
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {infrastructureOptions.map((option, idx) => {
              const colorMap = {
                blue: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400' },
                green: { bg: 'bg-brand-green/10', border: 'border-brand-green/30', text: 'text-brand-green' },
                purple: { bg: 'bg-purple-500/10', border: 'border-purple-500/30', text: 'text-purple-400' },
                orange: { bg: 'bg-orange-500/10', border: 'border-orange-500/30', text: 'text-orange-400' }
              };
              const colors = colorMap[option.color];
              const Icon = option.icon;
              return (
                <div
                  key={idx}
                  className={`bg-[#0d0d0d] rounded-xl p-6 border border-gray-800 hover:${colors.border} transition-colors`}
                >
                  <div className={`w-12 h-12 rounded-xl ${colors.bg} flex items-center justify-center mb-4`}>
                    <Icon className={`w-6 h-6 ${colors.text}`} />
                  </div>
                  <h3 className="text-white font-semibold mb-2">{option.title}</h3>
                  <p className="text-gray-400 text-sm">{option.description}</p>
                </div>
              );
            })}
          </div>
          <div className="text-center mt-6">
            <a
              href="/dedicated-server"
              className="inline-flex items-center gap-2 text-brand-green hover:text-brand-green/80 transition-colors"
            >
              En savoir plus sur les serveurs dédiés
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>

        {/* Contact CTA */}
        <div className="text-center bg-gradient-to-r from-brand-green/10 via-emerald-500/10 to-cyan-500/10 rounded-2xl p-10 border border-brand-green/20">
          <h3 className="text-2xl font-bold text-white mb-3">Besoin d'aide pour choisir&#x202F;?</h3>
          <p className="text-gray-400 mb-8 max-w-xl mx-auto">
            Notre équipe est disponible pour vous accompagner et répondre à toutes vos questions.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="/contact"
              className="inline-flex items-center gap-2 bg-brand-green hover:bg-brand-green/90 text-black font-semibold px-8 py-3.5 rounded-xl transition-all shadow-lg shadow-brand-green/20"
            >
              <Mail className="w-5 h-5" />
              Nous contacter
            </a>
            <a
              href="tel:+33415540000"
              className="inline-flex items-center gap-2 bg-[#111111] hover:bg-[#161616] text-white font-semibold px-8 py-3.5 rounded-xl border border-gray-800 hover:border-brand-green/30 transition-all"
            >
              <Headphones className="w-5 h-5 text-brand-green" />
              +33 4 15 54 00 00
            </a>
          </div>
        </div>
      </div>
    </main>
  );
};

export default PricingPage;
